import { FilterSidebar } from "@/components/layout/FilterSidebar";
import { NewSearchBar } from "@/components/layout/NewSearchBar";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { ZoneSelector } from "@/components/ZoneSelector";
import Seo from "@/components/seo/Seo";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { meetingCategories, type Meeting } from "@shared/schema";
import { StateCode, isValidStateCode } from "@shared/regions";
import {
  australianStates,
  getSuburbsByState,
  suburbsByState,
} from "@shared/suburbs";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  Calendar,
  Clock,
  Eye,
  Filter,
  MapPin,
  MessageCircle,
  Plus,
  User,
  Users,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";

// 주요 지역 (교민업체와 동일)
const regions = [
  { value: "ALL", label: "전체" },
  { value: "NSW", label: "시드니 (NSW)" },
  { value: "VIC", label: "멜번 (VIC)" },
  { value: "QLD", label: "브리즈번/케언즈 (QLD)" },
  { value: "SA", label: "아델레이드 (SA)" },
  { value: "WA", label: "퍼스 (WA)" },
  { value: "NT", label: "다윈 (NT)" },
  { value: "ACT", label: "캔버라 (ACT)" },
];

// 인기 검색어 (카테고리별)
const getPopularKeywords = (category?: string) => {
  const keywordMap: Record<string, string[]> = {
    sports: ["운동", "축구", "배드민턴", "테니스", "요가", "헬스"],
    hobby: ["독서", "영화", "음악", "사진", "그림", "요리"],
    study: ["스터디", "토익", "영어", "코딩", "자격증"],
    social: ["동네친구", "맘카페", "육아", "반려동물"],
    outdoor: ["등산", "캠핑", "여행", "아웃도어"],
    culture: ["전시회", "공연", "영화", "뮤지컬"],
    default: ["운동", "동네친구", "아웃도어", "독서", "음악", "취미"],
  };
  return keywordMap[category || "default"] || keywordMap["default"];
};

export default function Meetings() {
  const { selectedRegion: regionContextRegion, selectedSuburb, setSelectedRegion: setRegionContextRegion } = useRegion();
  const { selectedZoneId, selectedZone } = useZone();
  const [, navigate] = useLocation();
  // FilterSidebar에서 사용할 지역 상태 (RegionContext와 동기화)
  const [selectedRegion, setSelectedRegion] = useState<string>(regionContextRegion || "ALL");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // RegionContext와 동기화
  useEffect(() => {
    if (regionContextRegion) {
      setSelectedRegion(regionContextRegion);
    }
  }, [regionContextRegion]);

  // 지역 변경 시 RegionContext도 업데이트
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    if (setRegionContextRegion && isValidStateCode(region)) {
      setRegionContextRegion(region as StateCode);
    }
  };

  const { data: meetings, isLoading } = useQuery<Meeting[]>({
    queryKey: [
      "/api/meetings",
      regionContextRegion,
      selectedSuburb,
      selectedZoneId,
      selectedCategory,
      searchTerm,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      // limit 파라미터 명시적으로 추가 (validation 필수)
      params.set("limit", "50");
      params.set("zone", selectedZoneId);

      if (regionContextRegion && regionContextRegion !== "ALL") {
        params.set("state", regionContextRegion);
      }

      // suburb 필터는 선택적으로 적용 (데이터가 적을 수 있으므로)
      // 사용자가 명시적으로 선택한 경우에만 적용
      if (selectedSuburb && regionContextRegion) {
        // selectedSuburb는 ID이므로, 영문 이름으로 변환 (DB는 영문으로 저장됨)
        const suburbs = getSuburbsByState(regionContextRegion);
        const suburbInfo = suburbs.find((s) => s.id === selectedSuburb);
        // 영문 이름 우선 사용 (DB의 suburb 컬럼이 영문이므로)
        // 정규화 로직이 한글도 처리하지만, 영문 이름이 더 정확함
        const suburbName = suburbInfo?.name || selectedSuburb;
        params.set("suburb", suburbName);
      }

      if (selectedCategory !== "all") {
        params.set("category", selectedCategory);
      }
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }

      const response = await fetch(`/api/meetings?${params}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("[Meetings] API error:", response.status, errorText);
        throw new Error(`Failed to fetch meetings: ${response.status}`);
      }
      const data = await response.json();

      // API 응답 형식 처리: {ok: true, data: [...]} 또는 직접 배열
      const meetings = Array.isArray(data)
        ? data
        : data?.data || data?.meetings || [];

      // active 상태의 모임만 표시
      const activeMeetings = meetings.filter(
        (m: Meeting) => m.status === "active"
      );

      // suburb 필터를 적용했는데 결과가 없으면, state 필터만으로 재시도
      if (
        selectedSuburb &&
        activeMeetings.length === 0 &&
        regionContextRegion &&
        regionContextRegion !== "ALL"
      ) {
        console.log(
          "[Meetings] No results with suburb filter, retrying with state only"
        );
        const fallbackParams = new URLSearchParams();
        fallbackParams.set("limit", "50");
        fallbackParams.set("zone", selectedZoneId);
        fallbackParams.set("state", regionContextRegion);
        if (selectedCategory !== "all") {
          fallbackParams.set("category", selectedCategory);
        }
        if (searchTerm.trim()) {
          fallbackParams.set("search", searchTerm.trim());
        }

        const fallbackResponse = await fetch(`/api/meetings?${fallbackParams}`);
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          const fallbackMeetings = Array.isArray(fallbackData)
            ? fallbackData
            : fallbackData?.data || fallbackData?.meetings || [];
          return fallbackMeetings.filter((m: Meeting) => m.status === "active");
        }
      }

      return activeMeetings;
    },
  });

  const getCategoryLabel = (value: string) => {
    const cat = meetingCategories.find((c) => c.id === value);
    return cat ? cat.label : value;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // 지역 표시 이름 매핑 (주요 도시 이름 우선)
  const getDisplayLocation = () => {
    if (selectedSuburb) {
      const suburbInfo = Object.values(suburbsByState)
        .flat()
        .find((s) => s.id === selectedSuburb);
      return suburbInfo?.nameKo || selectedSuburb;
    }

    const stateInfo = australianStates.find((s) => s.id === regionContextRegion);
    if (!stateInfo || !regionContextRegion || regionContextRegion === "ALL") {
      return selectedZone.label;
    }

    // 주요 도시 이름 매핑
    const majorCityMap: Record<string, string> = {
      NSW: "시드니",
      VIC: "멜번",
      QLD: "브리즈번",
      SA: "아델레이드",
      WA: "퍼스",
      NT: "다윈",
      ACT: "캔버라",
      TAS: "호바트",
    };

    return majorCityMap[regionContextRegion] || stateInfo.nameKo || "전체 지역";
  };

  const displayLocation = getDisplayLocation();

  return (
    <>
      <Seo
        title="교민모임 — 관심사로 만나는 호주 한인 모임 | OZK"
        description="주최자, 날짜와 장소가 정해진 호주 한인 모임과 행사에 실제로 참여하세요."
        path="/meetings"
        keywords={[
          "모임",
          "커뮤니티",
          "동네친구",
          "스터디",
          "운동",
          "취미",
          "호주 한인",
        ]}
      />

      <div className="min-h-screen bg-background">
        {/* Search Section - NewSearchBar 사용 */}
        <NewSearchBar
          defaultCategory="meetup"
          onSearch={(query) => {
            setSearchQuery(query);
            setSearchTerm(query);
          }}
        />

        {/* Main Content */}
        <PageContainer size="default">
          <div className="space-y-5">
            <div className="text-xs text-muted-foreground">홈 &gt; 교민모임</div>
            <PageHeader
              title={`${displayLocation} 교민모임`}
              description="온라인 질문 게시판이 아니라 주최자, 일정과 장소를 확인하고 실제 참여하는 공간입니다."
              actions={<><ZoneSelector compact showSuburbs={false} /><Button onClick={() => navigate("/meetings/new")} size="sm" data-testid="button-create-post"><Plus className="mr-1 h-4 w-4" />교민모임 만들기</Button></>}
            />
            <div className="flex items-center justify-between gap-3 lg:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="lg:hidden"
                  data-testid="button-mobile-filter-toggle"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  필터
                </Button>
            </div>

            {/* Results Count */}
            {meetings && meetings.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {meetings.length}개 표시 중
                </p>
              </div>
            )}

            <div className="ozk-list-layout">
              {/* Sidebar - Filters (Desktop) - FilterSidebar 사용 */}
              <FilterSidebar
                sections={[
                  {
                    id: "region",
                    title: "주요 지역",
                    type: "text",
                    options: regions.map((r) => ({
                      id: r.value,
                      label: r.label,
                      value: r.value,
                    })),
                    selected: selectedRegion,
                    onSelect: (value) => handleRegionChange(value as string),
                  },
                  {
                    id: "category",
                    title: "카테고리",
                    type: "radio",
                    options: [
                      { id: "all", label: "전체", value: "all" },
                      ...meetingCategories.map((c) => ({
                        id: c.id,
                        label: c.label,
                        value: c.id,
                      })),
                    ],
                    selected: selectedCategory,
                    onSelect: (value) => setSelectedCategory(value as string),
                  },
                ]}
                onReset={() => {
                  handleRegionChange("ALL");
                  setSelectedCategory("all");
                }}
              />

              {/* Main Content - Meetings List */}
              <main className="ozk-list-main">
                {isLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Card key={i} className="p-4 animate-pulse">
                        <div className="flex gap-4">
                          <div className="w-20 h-20 bg-muted rounded-lg flex-shrink-0" />
                          <div className="flex-1">
                            <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                            <div className="h-4 bg-muted rounded w-full mb-2" />
                            <div className="h-3 bg-muted rounded w-1/2" />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : meetings && meetings.length > 0 ? (
                  <div className="space-y-3">
                    {meetings.map((meeting, index) => {
                      // 광고 카드 (6개마다)
                      const adCard =
                        index > 0 && index % 6 === 0 ? (
                          <Card
                            key={`ad-${index}`}
                            className="p-6 bg-muted/30 border-dashed"
                            data-testid={`ad-card-${index}`}
                          >
                            <div className="h-full flex flex-col items-center justify-center text-center space-y-3">
                              <div className="text-xs text-muted-foreground">
                                광고
                              </div>
                              <div className="text-sm font-medium text-muted-foreground">
                                광고 영역
                              </div>
                              <div className="text-xs text-muted-foreground">
                                귀하의 비즈니스를 여기에
                              </div>
                            </div>
                          </Card>
                        ) : null;

                      return (
                        <div key={meeting.id}>
                          {adCard}
                          <Link href={`/meetings/${meeting.id}`}>
                            <Card
                              className="p-4 hover-elevate cursor-pointer"
                              data-testid={`list-item-${meeting.id}`}
                            >
                              <div className="flex gap-4">
                                {/* Thumbnail - 왼쪽 */}
                                <div className="flex-shrink-0">
                                  {meeting.coverImage ? (
                                    <img
                                      src={meeting.coverImage}
                                      alt={meeting.title}
                                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                                    />
                                  ) : (
                                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-muted rounded-lg flex items-center justify-center">
                                      <Users className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>

                                {/* Content - 오른쪽 */}
                                <div className="flex-1 min-w-0">
                                  {/* Title with Category Badge */}
                                  <div className="flex items-start gap-2 mb-1">
                                    <h3 className="font-semibold text-base sm:text-lg line-clamp-1 flex-1">
                                      {meeting.title}
                                    </h3>
                                    <Badge
                                      variant="secondary"
                                      className="text-xs flex-shrink-0"
                                    >
                                      {getCategoryLabel(meeting.category)}
                                    </Badge>
                                  </div>

                                  {/* Description Preview */}
                                  <p className="text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                                    {meeting.description}
                                  </p>

                                  {/* Meta Info */}
                                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground">
                                    {meeting.location && (
                                      <div className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                        <span>{meeting.location}</span>
                                      </div>
                                    )}
                                    {!meeting.location &&
                                      (meeting.suburb || meeting.city) && (
                                        <div className="flex items-center gap-1">
                                          <MapPin className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                          <span>
                                            {[meeting.suburb, meeting.city]
                                              .filter(Boolean)
                                              .join(", ") || "전체 지역"}
                                          </span>
                                        </div>
                                      )}
                                    {(meeting as any).hostNickname && (
                                      <div className="flex items-center gap-1">
                                        <User className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                        <span>
                                          {(meeting as any).hostNickname}
                                        </span>
                                      </div>
                                    )}
                                    {meeting.nextMeetingDate &&
                                      meeting.meetingTime && (
                                        <div className="flex items-center gap-1">
                                          <Calendar className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                          <span>
                                            {formatDate(
                                              meeting.nextMeetingDate.toString()
                                            )}{" "}
                                            {meeting.meetingTime}
                                          </span>
                                        </div>
                                      )}
                                    {meeting.fee && (
                                      <div className="flex items-center gap-1">
                                        <span>💰</span>
                                        <span>{meeting.fee}</span>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1">
                                      <Users className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                      <span>
                                        {meeting.currentMembers || 0} /{" "}
                                        {meeting.capacity || "제한없음"} 명
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <MessageCircle className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                      <span>
                                        {meeting.postCount || 0} 개의 게시글
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Eye className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                      <span>{meeting.views || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3 text-[#663366] dark:text-[#9966cc]" />
                                      <span>
                                        {formatDistanceToNow(
                                          new Date(meeting.createdAt),
                                          {
                                            addSuffix: true,
                                            locale: ko,
                                          }
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Card className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">
                      아직 모임이 없습니다.
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      새로운 모임을 만들어 커뮤니티에 참여해보세요!
                    </p>
                    <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
                      <Button onClick={() => navigate("/meetings/new")}>
                        <Plus className="w-4 h-4 mr-2" />첫 교민모임 만들기
                      </Button>
                      <HelpRequestButton
                        sourceType="meeting"
                        zoneId={selectedRegion !== "ALL" ? selectedRegion : undefined}
                        suburb={selectedSuburb || undefined}
                        variant="outline"
                        label="원하는 모임, 카톡으로 물어보기"
                      />
                    </div>
                  </Card>
                )}
              </main>
            </div>
          </div>
        </PageContainer>

        {/* Mobile Filters */}
        <Dialog open={showMobileFilters} onOpenChange={setShowMobileFilters}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>필터</DialogTitle>
              <DialogDescription>
                모임을 필터링할 옵션을 선택하세요.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="mobile-category-filter">카테고리</Label>
                <RadioGroup
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                  className="space-y-1"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="mobile-category-all" />
                    <Label
                      htmlFor="mobile-category-all"
                      className="cursor-pointer text-sm font-normal"
                    >
                      전체
                    </Label>
                  </div>
                  {meetingCategories.map((cat) => (
                    <div key={cat.id} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={cat.id}
                        id={`mobile-category-${cat.id}`}
                      />
                      <Label
                        htmlFor={`mobile-category-${cat.id}`}
                        className="cursor-pointer text-sm font-normal"
                      >
                        {cat.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowMobileFilters(false)}>적용</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
